const buildTimestampMap = (points) => {
  const byTs = new Map();
  (points || []).forEach((point) => {
    if (point && point.ts != null) byTs.set(point.ts, point);
  });
  return byTs;
};

export const mergeDeviceDataByTimestamp = (existing = [], incoming = []) => {
  if (!Array.isArray(existing) && !Array.isArray(incoming)) return [];
  if (!Array.isArray(existing)) return incoming;
  if (!Array.isArray(incoming) || incoming.length === 0) return existing;

  const byTs = buildTimestampMap(existing);
  incoming.forEach((point) => {
    if (point && point.ts != null) byTs.set(point.ts, point);
  });
  return Array.from(byTs.values()).sort((a, b) => b.ts - a.ts); // Sort by timestamp descending in Devices
};

export const mergeConnectedDevices = (incoming = [], existing = []) => {
  const existingMap = new Map((existing || []).map((device) => [device?.id, device]));

  return (incoming || []).map((device) => {
    const prior = existingMap.get(device?.id) || {};
    const mergedData = mergeDeviceDataByTimestamp(prior?.data || [], device?.data || []);
    return {
      ...device,
      data: mergedData,
    };
  });
};

export const normalizeDataPoints = (dataPoints) => {
  const normalized = [];
  if (Array.isArray(dataPoints)) {
    dataPoints.forEach((item) => {
      if (Array.isArray(item)) {
        normalized.push(item);
      } else if (item && Array.isArray(item.data)) {
        normalized.push(item.data);
      } else if (item && Array.isArray(Object.values(item)[0])) {
        const arr = Object.values(item).find((v) => Array.isArray(v));
        if (arr) normalized.push(arr);
      }
    });
  } else if (dataPoints && typeof dataPoints === 'object') {
    Object.values(dataPoints).forEach((value) => {
      if (Array.isArray(value)) normalized.push(value);
    });
  }
  return normalized;
};

export const mergeDataPointsIntoDevices = (devices = [], dataPoints = []) => {
  const normalized = normalizeDataPoints(dataPoints);

  // Build a Map<uid, points[]> so each device lookup is O(1) instead of O(n)
  const pointsByUid = new Map();
  for (const points of normalized) {
    if (!Array.isArray(points) || points.length === 0) continue;
    const uid = points.find((p) => p?.uid != null)?.uid;
    if (uid != null) pointsByUid.set(uid, points);
  }

  return (devices || []).map((device) => {
    if (device?.device_type === 'Overwatch VMS') return device;

    const matching = pointsByUid.get(device?.id);

    const existing = Array.isArray(device?.data) ? device.data : [];
    const merged = Array.isArray(matching)
      ? mergeDeviceDataByTimestamp(existing, matching)
      : existing;

    return {
      ...device,
      data: merged,
    };
  });
};

export const computeDataRangeFromDevices = (devices = [], fallbackStart, fallbackEnd) => {
  let minTs = Number.isFinite(fallbackStart) ? fallbackStart : null;
  let maxTs = Number.isFinite(fallbackEnd) ? fallbackEnd : null;

  (devices || []).forEach((device) => {
    const points = Array.isArray(device?.data) ? device.data : [];
    points.forEach((point) => {
      const ts = point?.ts;
      if (typeof ts !== 'number') return;
      if (minTs == null || ts < minTs) minTs = ts;
      if (maxTs == null || ts > maxTs) maxTs = ts;
    });
  });

  return { dataStartTime: minTs, dataEndTime: maxTs };
};
